import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { Link } from 'react-router-dom'

import { api } from '../../lib/api'
import { useCategoryNames, useOperatorTimezone } from '../../lib/operator'
import type { InboxItem, InboxPage } from '../../lib/optypes'
import { shortDate, shortDateTime } from '../../lib/optypes'

import { OperatorLayout } from './OperatorLayout'

const STATUSES = [
  ['', 'All'],
  ['new', 'New'],
  ['scheduled', 'Scheduled'],
  ['workspace_in_progress', 'In workspace'],
  ['packet_sent', 'Packet sent'],
  ['visited', 'Visited'],
  ['quoted', 'Quoted'],
  ['won', 'Won'],
  ['lost', 'Lost'],
] as const

/** The inbox (01 §4). One row per request, newest first, with the two facts that decide what
 *  Andrew does next: is there a visit booked, and is it in the service area. */
export function Inbox() {
  const [status, setStatus] = useState('')
  const tz = useOperatorTimezone()
  const names = useCategoryNames()

  const { data, isPending, isError } = useQuery({
    queryKey: ['op-inbox', status],
    queryFn: () => api.get<InboxPage>(`/op/requests${status ? `?status=${status}` : ''}`),
    refetchInterval: 60_000,
  })

  return (
    <OperatorLayout>
      <h1 className="text-2xl">Inbox</h1>

      <div className="mt-5 flex flex-wrap gap-1.5">
        {STATUSES.map(([value, label]) => {
          const count = value ? (data?.counts[value] ?? 0) : undefined
          const active = status === value
          return (
            <button
              key={value || 'all'}
              type="button"
              onClick={() => setStatus(value)}
              aria-pressed={active}
              className={`inline-flex min-h-10 items-center gap-1.5 rounded-pill border px-4 text-sm font-semibold transition-colors ${
                active
                  ? 'border-blue bg-blue-tint text-blue-press'
                  : 'border-line bg-white text-body hover:border-line-2'
              }`}
            >
              {label}
              {count !== undefined && count > 0 && (
                <span className="ml-1.5 font-mono text-xs text-muted">{count}</span>
              )}
            </button>
          )
        })}
      </div>

      {isPending && <p className="mt-8 text-muted">Loading…</p>}
      {isError && <p className="mt-8 text-muted">Could not load the inbox. Reload to retry.</p>}

      {data && data.items.length === 0 && (
        <p className="mt-8 rounded-card border border-line bg-white p-6 text-muted">
          Nothing here{status ? ' with that status' : ' yet'}.
        </p>
      )}

      <ul className="m-0 mt-5 grid list-none gap-2.5 p-0">
        {data?.items.map((item) => (
          <li key={item.id}>
            <Row item={item} tz={tz} names={names} />
          </li>
        ))}
      </ul>
    </OperatorLayout>
  )
}

function Row({
  item,
  tz,
  names,
}: {
  item: InboxItem
  tz: string
  names: (ids: string[]) => string
}) {
  return (
    <Link
      to={`/op/requests/${item.id}`}
      className="block rounded-card border border-line bg-white p-4 transition-colors hover:border-line-2"
    >
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <span className="font-semibold text-ink">{item.homeowner_name ?? 'No name given'}</span>
        <span className="font-mono text-xs text-muted">{shortDate(item.created_at, tz)}</span>
      </div>

      <p className="m-0 mt-1 text-sm text-body">
        {names(item.categories)}
        {item.town ? ` — ${item.town}` : ''}
      </p>

      <div className="mt-2.5 flex flex-wrap gap-1.5 text-xs">
        <Tag tone={item.outcome ? 'solid' : 'quiet'}>
          {item.outcome ?? item.status.replace(/_/g, ' ')}
        </Tag>
        {item.booking_start ? (
          <Tag tone="blue">{shortDateTime(item.booking_start, tz)}</Tag>
        ) : (
          <Tag tone="warn">no visit booked</Tag>
        )}
        {item.photo_count > 0 && (
          <Tag tone="quiet">
            {item.photo_count} photo{item.photo_count > 1 ? 's' : ''}
          </Tag>
        )}
        {item.in_service_area === false && <Tag tone="warn">outside Monmouth</Tag>}
      </div>
    </Link>
  )
}

function Tag({
  children,
  tone,
}: {
  children: React.ReactNode
  tone: 'quiet' | 'blue' | 'warn' | 'solid'
}) {
  const tones = {
    quiet: 'border-line bg-paper text-muted',
    blue: 'border-blue bg-blue-tint text-blue-press',
    warn: 'border-line-2 bg-sand text-ink',
    solid: 'border-navy bg-navy text-white',
  }
  return (
    <span className={`rounded-pill border px-2.5 py-0.5 font-semibold ${tones[tone]}`}>
      {children}
    </span>
  )
}
